import { urlFor } from "@/utils/client";
import { ITestimonials } from "@/utils/types";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { SliderArrows } from "./sliderArrows";

export function Testimonials(props: ITestimonials) {
  const testimonials = props.testimonials ?? [];
  const [currentSlide, setCurrentSlide] = useState(0);

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: testimonials.length > 1,
    breakpoints: {
      "(max-width: 768px)": {
        slides: {
          perView: testimonials.length > 1 ? 1.15 : 1,
          spacing: 16,
          origin: "center",
        },
      },
    },
    slides: {
      perView: Math.min(testimonials.length || 1, 3.4),
      spacing: 30,
      origin: "center",
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel ?? 0);
    },
  });

  useEffect(() => {
    if (testimonials.length === 0) return;
    setCurrentSlide(instanceRef.current?.track?.details?.rel ?? 0);
  }, [instanceRef, testimonials.length]);

  return (
    <div className="flex flex-col items-center component-gap w-full overflow-hidden text-center">
      <div
        style={{
          color: props.accentColor ? props.accentColor : "#000",
        }}
        className="flex flex-col component-gap max-w-5xl px-4"
      >
        <p className="heading">{props.title}</p>
        <p className="opacity-80">{props.description}</p>
      </div>
      <div ref={sliderRef} className="keen-slider w-full">
        {testimonials.map((testimonial, i) => {
          return (
            <div
              key={i}
              onClick={() => {
                if (!instanceRef.current) return;
                if (i === currentSlide) return;
                const total = testimonials.length;
                const forwardDist = (i - currentSlide + total) % total;
                const backwardDist = (currentSlide - i + total) % total;
                if (forwardDist <= backwardDist) {
                  instanceRef.current.next();
                } else {
                  instanceRef.current.prev();
                }
              }}
              className="keen-slider__slide cursor-pointer"
            >
              <div
                className={`p-8 bg-white gap-8 flex flex-col items-start h-full transition-all duration-300 origin-center ${
                  currentSlide !== i
                    ? "scale-[0.97] opacity-75"
                    : "scale-100 opacity-100"
                }`}
              >
                {testimonial.image?.image && (
                  <Image
                    className="aspect-[16/9] w-full object-cover object-top"
                    width={640}
                    height={360}
                    sizes="(max-width: 768px) 100vw, 33vw"
                    alt={testimonial.image?.caption ?? ""}
                    src={urlFor(testimonial.image.image).url()}
                  />
                )}
                <p className="text-black text-left">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
                <div
                  className="mt-auto flex w-full flex-col items-start gap-0.5 text-left"
                  style={{
                    color: props.accentColor ? props.accentColor : "#000",
                  }}
                >
                  <p className="font-display text-lg">{testimonial.name}</p>
                  {[testimonial.role, testimonial.company].some(
                    (s) => typeof s === "string" && s.trim() !== ""
                  ) && (
                    <p
                      style={{
                        opacity: 0.75,
                        color: props.accentColor ? props.accentColor : "#000",
                      }}
                      className="font-body text-base"
                    >
                      {[testimonial.role, testimonial.company]
                        .filter((s) => typeof s === "string" && s.trim() !== "")
                        .join(" · ")}
                    </p>
                  )}
                </div>
                {testimonial.button?.link && (
                  <div className="flex italic">
                    {testimonial.button.link.includes("http") ? (
                      <a
                        className="flex items-center gap-2"
                        target="_blank"
                        rel="noreferrer"
                        href={testimonial.button.link}
                      >
                        {testimonial.button.label}
                        <svg
                          width="10"
                          height="11"
                          viewBox="0 0 10 11"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7.24358 6.16363H0.214722V4.83655H7.24358L4.05364 1.6466L4.99997 0.714844L9.78521 5.50009L4.99997 10.2853L4.05364 9.35357L7.24358 6.16363Z"
                            fill="#000"
                          />
                        </svg>
                      </a>
                    ) : (
                      <Link
                        className="flex items-center gap-2"
                        href={testimonial.button.link}
                      >
                        {testimonial.button.label}
                        <svg
                          width="10"
                          height="11"
                          viewBox="0 0 10 11"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7.24358 6.16363H0.214722V4.83655H7.24358L4.05364 1.6466L4.99997 0.714844L9.78521 5.50009L4.99997 10.2853L4.05364 9.35357L7.24358 6.16363Z"
                            fill="#000"
                          />
                        </svg>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <SliderArrows
        onPrev={() => instanceRef.current?.prev()}
        onNext={() => instanceRef.current?.next()}
      />
    </div>
  );
}
